import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import {
  extractWizProperty,
  formatWizTimestamp,
  getExposureLevelClass,
  getHttpStatusClass,
  parseWizFinding,
} from '@/sections/poe/processors/wiz/helpers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
export function renderEndpoint(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;
  if (
    wizData &&
    wizData['endpoint'] &&
    !wizData['security tool finding'] &&
    !wizData['vulnerability'] &&
    mainTabKeys.includes('ATTRIBUTES')
  ) {
    const endpoint = wizData['endpoint'];
    const endpointData =
      typeof endpoint === 'string' ? parseWizFinding(endpoint) : endpoint;
    const endpointName =
      extractWizProperty(endpointData, 'name') ||
      extractWizProperty(endpointData, 'Name') ||
      '';
    const endpointType =
      extractWizProperty(endpointData, 'type') ||
      extractWizProperty(endpointData, 'Type') ||
      '';
    const cloudPlatform =
      extractWizProperty(endpointData, 'cloudPlatform') ||
      extractWizProperty(endpointData, 'Cloud Platform') ||
      '';
    const protocols =
      extractWizProperty(endpointData, 'protocols') ||
      extractWizProperty(endpointData, 'Protocols') ||
      '';
    const httpStatus = extractWizProperty(endpointData, 'httpGETStatus') || '';
    const exposureLevel =
      extractWizProperty(endpointData, 'exposureLevel_name') ||
      extractWizProperty(endpointData, 'Exposure Level Name') ||
      '';
    const exposureDescription =
      extractWizProperty(endpointData, 'exposureLevel_description') || '';
    const host =
      extractWizProperty(endpointData, 'host') ||
      extractWizProperty(endpointData, 'finalHost') ||
      '';
    const path = extractWizProperty(endpointData, 'path') || '';
    const subscriptionId =
      extractWizProperty(endpointData, 'subscriptionExternalId') || '';
    const externalId = extractWizProperty(endpointData, 'externalId') || '';
    const updatedAt = extractWizProperty(endpointData, 'updatedAt') || '';
    const productIds = extractWizProperty(endpointData, 'productIDs') || '';
    sections['wiz_endpoint'] = (
      <div className="space-y-4">
        <div className="border-chariot-stroke rounded-lg border">
          <div className="border-chariot-stroke border-b px-4 py-3">
            <h4 className="text-chariot-text-primary font-semibold">
              Endpoint Details
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {endpointType && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Type:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary">
                    {endpointType}
                  </span>
                  <CopyToClipboard
                    textToCopy={endpointType}
                    alwaysShowCopyIcon
                  />
                </div>
              </div>
            )}
            {endpointName && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Name:
                </span>
                <div className="flex max-w-md items-center gap-2">
                  <span className="text-chariot-text-primary text-right font-mono text-sm break-all">
                    {endpointName}
                  </span>
                  <CopyToClipboard
                    textToCopy={endpointName}
                    alwaysShowCopyIcon
                  />
                </div>
              </div>
            )}
            {host && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Host:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary">{host}</span>
                  <CopyToClipboard textToCopy={host} alwaysShowCopyIcon />
                </div>
              </div>
            )}
            {path && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Path:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary font-mono">
                    {path}
                  </span>
                  <CopyToClipboard textToCopy={path} alwaysShowCopyIcon />
                </div>
              </div>
            )}
            {protocols && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Protocols:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary">{protocols}</span>
                  <CopyToClipboard textToCopy={protocols} alwaysShowCopyIcon />
                </div>
              </div>
            )}
            {httpStatus && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  HTTP Status:
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-medium ${getHttpStatusClass(httpStatus)}`}
                  >
                    {httpStatus}
                  </span>
                  <CopyToClipboard textToCopy={httpStatus} alwaysShowCopyIcon />
                </div>
              </div>
            )}
            {cloudPlatform && (
              <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Cloud Platform:
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-primary">
                    {cloudPlatform}
                  </span>
                  <CopyToClipboard
                    textToCopy={cloudPlatform}
                    alwaysShowCopyIcon
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {(exposureLevel || exposureDescription) && (
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Security & Exposure
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {exposureLevel && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Exposure Level:
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${getExposureLevelClass(exposureLevel)}`}
                    >
                      {exposureLevel}
                    </span>
                    <CopyToClipboard
                      textToCopy={exposureLevel}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {exposureDescription && (
                <div className="py-2">
                  <div className="mb-2 flex items-start justify-between">
                    <span className="text-chariot-text-secondary font-medium">
                      Exposure Description:
                    </span>
                    <CopyToClipboard
                      textToCopy={exposureDescription}
                      alwaysShowCopyIcon
                    />
                  </div>
                  <p className="text-chariot-text-primary text-sm">
                    {exposureDescription}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {(subscriptionId || externalId || updatedAt || productIds) && (
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Additional Properties
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {subscriptionId && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Subscription ID:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary font-mono">
                      {subscriptionId}
                    </span>
                    <CopyToClipboard
                      textToCopy={subscriptionId}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {externalId && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    External ID:
                  </span>
                  <div className="flex max-w-md items-center gap-2">
                    <span className="text-chariot-text-primary text-right font-mono text-sm break-all">
                      {externalId}
                    </span>
                    <CopyToClipboard
                      textToCopy={externalId}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {updatedAt && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Updated:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary">
                      {formatWizTimestamp(updatedAt)}
                    </span>
                    <CopyToClipboard
                      textToCopy={updatedAt}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
              {productIds && (
                <div className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0">
                  <span className="text-chariot-text-secondary font-medium">
                    Product IDs:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-chariot-text-primary font-mono text-sm">
                      {productIds}
                    </span>
                    <CopyToClipboard
                      textToCopy={productIds}
                      alwaysShowCopyIcon
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
    tempTabs['ATTRIBUTES'].push({ label: 'Endpoint', value: 'wiz_endpoint' });
  }
}
